// TODO: Remove this file when "Finding status tags" feature flag is removed
import { Box, Button } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useDialogs } from "@toolpad/core";
import {
  FindingException,
  GetCasesCountDocument,
  GetFindingsDocument,
  ScoreRating,
} from "~/operations";
import { isSpaceOrWorkspaceScope, ScopeType, useScope } from "~/hooks/useScope";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { useTickets } from "~/pages/tickets/hooks";
import { useTicketingIntegrations } from "~/pages/tickets/hooks/useTicketingIntegrations";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";
import { TriageBarProps } from "~/components/TriageBar/TriageBar";
import { TakeActionButton } from "~/components/TakeAction/TakeActionButton";
import { getStatusDisplay } from "~/components/FirewatchRow/Cells/firewatchTableCellUtils";
import { AggregateScoresNodeCasesEdge } from "~/components/FirewatchPage";
import {
  ExceptionFormInput,
  SetExceptionDialog,
} from "~/components/exceptions/SetExceptionDialog";
import { RemoveExceptionsGroupDialog } from "~/components/exceptions/RemoveExceptionsGroupDialog";
import { CreateTicketDialog } from "~/pages/tickets/components/CreateTicketDialog";
import { CreateSecurityPipelinePrDialog } from "~/pages/integrations/security-pipeline/components/CreatePullRequest/CreateSecurityPipelinePRDialog";
import { AdvisoryFindingsNode } from "~/pages/inventory/components/Findings/FindingsTable/utils/advisoryTypes";
import { CVEFindingsNode } from "~/pages/inventory/components/AssetSoftwareDetails/AssetSoftwareVulnerabilities/hooks/types";
import { FindingRow, ActionContext } from "~/components/FindingsTable";

type LegacyActionCellProps = {
  row: FindingRow;
  actionContext: ActionContext;
};

export function LegacyActionCell({
  row,
  actionContext,
}: LegacyActionCellProps) {
  const { isFeatureEnabled } = useFeatureFlags();
  const { spaceScope, activeScope } = useScope();
  const dialogs = useDialogs();

  const {
    handleCreateTicketingIntegrationModalOpen,
    loading: createCaseLoading,
    handleCreateCase,
    hasCreateTicketPermissions,
  } = useTickets({
    createCaseRefetchQueries: [GetCasesCountDocument],
  });

  const { hasTicketingIntegration } = useTicketingIntegrations();
  const { hasSecurityPipelineIntegrations } = useSecurityPipelineIntegrations();

  const isCheckFinding = row.__typename === "CheckFinding";
  const isAdvisoryFinding = row.__typename === "AdvisoryFinding";

  const {
    handleRemoveExceptionGroups,
    handleSetException,
    loading: exceptionsLoading,
  } = useExceptions({
    scopeMrns: [activeScope?.mrn || ""],
    ...(isAdvisoryFinding ? { advisoryMrns: [row.mrn] } : {}),
    ...(isCheckFinding ? { queryMrns: [row.mrn] } : {}),
    ...(!isCheckFinding && !isAdvisoryFinding ? { cveMrns: [row.mrn] } : {}),
    refetchQueries: [GetFindingsDocument],
  });

  // Branch 1 — GenericFinding → return null
  if (row.__typename === "GenericFinding") {
    return null;
  }

  // Branch 2 — Has existing status (ticket or exception) → Button link
  const status = getStatusDisplay({
    exception: row.exception as
      | Pick<FindingException, "action" | "createdAt" | "exceptionId">
      | null
      | undefined,
    cases: row.cases?.edges as AggregateScoresNodeCasesEdge[] | undefined,
    scope: isSpaceOrWorkspaceScope(activeScope) ? activeScope : undefined,
    spaceScope,
  });

  if (status) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Button
          component={RouterLink}
          to={status.url || ""}
          startIcon={status.icon}
          variant="text"
          size="small"
          onClick={(e) => e.stopPropagation()}
          sx={{
            color: row.exception ? "text.secondary" : "text.primary",
            textWrap: "nowrap",
            height: 30,
          }}
        >
          {status.genericLabel}
        </Button>
      </Box>
    );
  }

  // Branch 3 — No status → TakeActionButton with remediation hooks
  const target = isCheckFinding
    ? "check"
    : isAdvisoryFinding
      ? "advisory"
      : "cve";

  const dialogTitle =
    row.__typename === "CheckFinding"
      ? row.title || ""
      : row.id || "this finding";

  const handleCreateTicketDialogOpen = () => {
    if (!spaceScope || !activeScope || !isSpaceOrWorkspaceScope(activeScope))
      return;

    dialogs.open(CreateTicketDialog, {
      isCombinedTicket: true,
      target: "asset",
      loading: createCaseLoading,
      onSave: (data) =>
        handleCreateCase({
          data,
          refetchQueries: [GetFindingsDocument],
        }),
      selectedEntities: [
        { mrn: row.mrn || "", scopeMrn: activeScope?.mrn || "" },
      ],
      space: spaceScope,
      scope: activeScope,
      hasCreateCasePermissions: hasCreateTicketPermissions,
    });
  };

  const handleSetExceptionModalOpen = () => {
    dialogs.open(SetExceptionDialog, {
      target,
      selectedEntities: [
        {
          mrn: row.mrn,
          rating: row.rating || ScoreRating.Unscored,
          title: row.title || "",
        },
      ],
      onSave: (params: ExceptionFormInput) => handleSetException(params),
      targetData: {
        targetTitle: dialogTitle,
        onAsset: {
          id: actionContext.assetId,
          mrn: actionContext.assetMrn || "",
          name: actionContext.assetName || "",
          __typename: "EntityInfoAsset",
        },
      },
    });
  };

  const handleRemoveExceptionModalOpen = () => {
    dialogs.open(RemoveExceptionsGroupDialog, {
      onRemove: handleRemoveExceptionGroups,
      exceptionMrns: [row.exception?.exceptionMrn].filter(
        (mrn): mrn is string => Boolean(mrn),
      ),
    });
  };

  const isRemediationSupported = ["AdvisoryFinding", "CveFinding"].includes(
    row.__typename,
  );

  const handleSecurityPipelinePRDialogOpen = () => {
    if (!spaceScope || !isRemediationSupported) return;
    // FindingRow is a slimmed-down type, but at runtime the data is the full finding
    // node from GraphQL. This cast is acceptable for this temporary legacy component.
    // TODO: Remove this file when "Finding status tags" feature flag is removed
    dialogs.open(CreateSecurityPipelinePrDialog, {
      findings: [row as unknown as AdvisoryFindingsNode | CVEFindingsNode],
      spaceScope,
      ...(actionContext.assetMrn
        ? { assetMrns: [actionContext.assetMrn] }
        : {}),
    });
  };

  const availableRemediations: TriageBarProps["availableRemediations"] = {
    ticket: {
      active: true,
      onClick: hasTicketingIntegration
        ? handleCreateTicketDialogOpen
        : handleCreateTicketingIntegrationModalOpen,
    },
    securityPipeline: {
      active:
        isRemediationSupported &&
        isFeatureEnabled("Security Pipeline") &&
        hasSecurityPipelineIntegrations,
      onClick: handleSecurityPipelinePRDialogOpen,
    },
    exception: {
      active: spaceScope?.type === ScopeType.Space,
      hasException: !!row.exception,
      onClick: !!row.exception
        ? handleRemoveExceptionModalOpen
        : handleSetExceptionModalOpen,
      disabled: exceptionsLoading,
    },
  };

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: "relative",
        zIndex: 1,
        display: "flex",
        alignItems: "center",
        height: "100%",
      }}
    >
      <TakeActionButton
        availableRemediations={availableRemediations}
        type="action-cell"
      />
    </Box>
  );
}
